import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  forwardRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { ActivatedRoute, Router } from '@angular/router';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import {
  Observable,
  distinctUntilChanged,
  first,
  forkJoin,
  map,
  mergeMap,
  of,
  catchError,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { BottomSheetOverviewComponent } from '../subscription-overview/bottom-sheet-overview/bottom-sheet-overview.component';
import { TemplatesComponent } from '../templates-component/templates-component';

import { ProductDao } from '@app/dao/product/product.dao';
import { UserDao } from '@app/dao/user/user.dao';
import { SimulationDao } from '@app/dao/simulation/simulation.dao';
import { SimulationType } from '@app/dao/simulation/simulation.interface';
import { ProductsTypeEnum } from '@app/forms/subscription/definition/interface';
import { UserService } from '@app/providers/user/user.service';
import {
  BuFeature,
  CustomerActivityDao,
} from '@app/dao/customer-activity/customer-activity.dao';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MutuelleDevisData, MutuelleHypothesesData, PledgeRead, PrevoyanceDevisData, PrevoyanceHypothesesData } from '../definition/interface/Pledge';
import { LoadingService } from '../services/loading.service';
import { SubscriptionFacadeService } from '../services/subscription-facade.service';
import { BaseSubscriptionComponent } from './BaseSubscriptionComponent';

const FRANCHISE_DELAI_LABELS: Record<string, string> = {
  D_7_0_0: '7/0/0', D_7_7_7: '7/7/7',
  D_15_3_3: '15/3/3', D_15_15_15: '15/15/15',
  D_30_0_0: '30/0/0', D_30_3_0: '30/3/0', D_30_3_3: '30/3/3', D_30_30_30: '30/30/30',
  D_60_0_0: '60/0/0', D_60_3_3: '60/3/3', D_60_60_60: '60/60/60',
  D_90_0_0: '90/0/0', D_90_3_3: '90/3/3', D_90_90_90: '90/90/90',
  D_1_09_31: '1/09/31',
};

const DELAI_ATTENTE_LABELS: Record<string, string> = {
  D_0_0: '0/0', D_1_0: '1/0', D_3_0: '3/0', D_3_1: '3/1',
  D_6_0: '6/0', D_6_3: '6/3',
  D_0_0_3: '0/0/3', D_3_0_09: '3/0/09', D_3_0_12: '3/0/12', D_6_0_6: '6/0/6',
};

const SEUIL_DECLENCHEMENT_LABELS: Record<string, string> = {
  S_15: '15', S_20: '20', S_33: '33', S_50: '50', S_66: '66',
};

@UntilDestroy()
@Component({
    selector: 'ea-subscription-forms',
    templateUrl: './ea-subscription-forms.component.html',
    styleUrls: ['./ea-subscription-forms.component.scss'],
    encapsulation: ViewEncapsulation.None,
    //changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: PrFormAbstract,
            useExisting: forwardRef(() => EaSubscriptionFormsComponent),
        },
    ],
    standalone: false
})
export class EaSubscriptionFormsComponent
  extends BaseSubscriptionComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('cellTemplates', { static: true })
  cellTemplates: TemplatesComponent;

  showSidebarStepper: boolean = true;

  canSubscribe$ = this.customerActivityDao.canUse$(BuFeature.SUBSCRIPTION);

  constructor(
    private route: ActivatedRoute,
    private _bottomSheet: MatBottomSheet,
    private cdr: ChangeDetectorRef,
    private userService: UserService,
    public subscriptionFacade: SubscriptionFacadeService,
    private loadingService: LoadingService,
    private simulationDao: SimulationDao,
    private productDao: ProductDao,
    private userDao: UserDao,
    private router: Router,
    private customerActivityDao: CustomerActivityDao,
  ) {
    super(subscriptionFacade);
  }

  ngOnInit() {
    this.subscriptionFacade.showSidebarStepper$
      .pipe(untilDestroyed(this))
      .subscribe((show: boolean) => {
        this.showSidebarStepper = show;
      });
    this.initPledgeOptions();
    this.checkEntity();
    this.prefillFromDevisIfNeeded();
    this.initQuestions();
    this.loadingService;
  }
  initPledgeOptions() {
    this.subscriptionFacade
      .getPledgeOptions$()
      .pipe(take(1))
      .subscribe(options => {
        this.subscriptionFacade.setPledgeOptions(options);
      });
  }

  getTemplate(ngTemplateName: string) {
    return this.cellTemplates.getTemplate(ngTemplateName);
  }

  openBottomSheet(): void {
    this._bottomSheet.open(BottomSheetOverviewComponent);
  }
  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }
  get loading$() {
    return this._loading$;
  }
  get error$() {
    return this._error$;
  }

  checkEntity() {
    const productType = this.route.snapshot.paramMap.get('id') as any;
    const pledgeId = this.route.snapshot.queryParams.pledgeId;
    this.userService
      .getUser()
      .pipe(
        take(1),
        tap(user => this.setPictureFile(user)),
        switchMap(user => {
          return this.subscriptionFacade.pledgesWithCRUD$.pipe(
            untilDestroyed(this),
            map((pledges: PledgeRead[]) =>
              pledges?.find(pledge => pledge?.id == pledgeId),
            ),
            tap(pledge => this.handlePledgeUpdate(productType, pledge, user)),
            first((pledge: any) => {
              if (!pledge) this.noPledgeFound = !pledge;

              this.displayInvalidQuestionForClient(user, pledge, productType);

              return pledge;
            }),
          );
        }),
      )
      .subscribe();
  }

  private getProductTypeFromRoute(): ProductsTypeEnum {
    return this.route.snapshot.paramMap.get('id') as ProductsTypeEnum;
  }
  private simulateAssuranceVie(productType: ProductsTypeEnum) {
    if (productType === ProductsTypeEnum.ASSURANCE_VIE) {
      this.simulationDao
        .simulate(undefined, SimulationType.ASSURANCE_VIE, {})
        .pipe(take(1))
        .subscribe();
    }
  }
  initQuestions() {
    const productType = this.getProductTypeFromRoute();
    this.simulateAssuranceVie(productType);

    this.question$ = this.initializeQuestionStream(productType);
  }

  private initializeQuestionStream(productType: ProductsTypeEnum) {
    return this.userService.getUser().pipe(
      take(1),
      tap(user => this.handleUserUpdate(user)),
      switchMap(user => {
        return this.subscriptionFacade.stepChanged$.pipe(
          distinctUntilChanged(),
          mergeMap(step => this.handleStepChange(step)),

          tap(entity => this.handleEntityUpdate(entity)),

          switchMap(() => this.handleQuestionsChanged(user, productType)),
          distinctUntilChanged(),

          tap(questions =>
            this.handleNextQuestionGeneration(questions, productType),
          ),
          map(questions => this.updateQuestionState(questions)),
        );
      }),
    );
  }
  navigateToSubscription() {
    this.router.navigate(['/dashboard/souscription']);
  }
  ngOnDestroy(): void {
    this.subscriptionFacade.clearStep();
    this.subscriptionFacade.clearEntity();
    this.subscriptionFacade.clearPledge();
    this.subscriptionFacade.setIsProgress(false);
  }

  private getActivePledge$(): Observable<PledgeRead> {
    return this.subscriptionFacade.pledgesWithCRUD$.pipe(
      map((pledges: PledgeRead[]) =>
        pledges?.find(p => p?.id == this.route.snapshot.queryParams.pledgeId),
      ),
      first(pledge => !!pledge),
    );
  }

  private resolveInsuranceNameData$(enfantsRattachesIds: number[]): Observable<{ enfantsNoms: string[]; conjointNom: string | null }> {
    return forkJoin({
      children: this.userDao.getChildren().pipe(take(1), catchError(() => of([]))),
      couple: this.userDao.getCouple().pipe(take(1), catchError(() => of([]))),
    }).pipe(
      map(({ children, couple }) => ({
        enfantsNoms: this.resolveEnfantsNoms(enfantsRattachesIds, children),
        conjointNom: this.resolveConjointNom(couple),
      })),
    );
  }

  private prefillFromDevisIfNeeded(): void {
    const devisId = this.route.snapshot.queryParams.devisId;
    const ownerId = this.route.snapshot.queryParams.ownerId;
    const productType = this.getProductTypeFromRoute();

    // Sans devis : charger les hypothèses et les defaults produit en un seul putPledge
    if (!devisId) {
      if (productType === ProductsTypeEnum.CONTINGENCY || productType === ProductsTypeEnum.INSURANCE) {
        this.prefillWithoutDevis(productType);
      }
      return;
    }

    // BORROWER_INSURANCE : pré-remplir depuis le devis AE (endpoint dédié)
    if (productType === ProductsTypeEnum.BORROWER_INSURANCE) {
      this.getActivePledge$().pipe(
        switchMap((pledge: PledgeRead) => {
          const accountId = Number(pledge.accountId);
          return this.productDao.getAeDevisById(accountId, Number(devisId)).pipe(
            map(devis => ({ pledge, devis })),
            catchError(() => of({ pledge, devis: null })),
          );
        }),
        take(1),
      ).subscribe(({ pledge, devis }) => {
        if (!devis) return;
        this.mergeAeDevisDataIntoPledge(pledge, devis);
      });
      return;
    }

    // CONTINGENCY / INSURANCE : pré-remplir depuis le devis + hypothèses
    this.getActivePledge$().pipe(
      switchMap((pledge: PledgeRead) => {
        const accountId = Number(pledge.accountId);
        const { devis$, hypotheses$ } = this.getDevisAndHypothesesObservables(
          productType, accountId, Number(devisId), ownerId ? Number(ownerId) : null,
        );
        return forkJoin({ devis: devis$, hypotheses: hypotheses$ }).pipe(
          map(({ devis, hypotheses }) => ({ pledge, devis, hypotheses })),
        );
      }),
      take(1),
      switchMap(({ pledge, devis, hypotheses }) => {
        if (productType === ProductsTypeEnum.INSURANCE) {
          return this.resolveInsuranceNameData$(hypotheses?.enfantsRattachesIds).pipe(
            map(({ enfantsNoms, conjointNom }) => ({ pledge, devis, hypotheses, enfantsNoms, conjointNom })),
          );
        }
        return of({ pledge, devis, hypotheses, enfantsNoms: [] as string[], conjointNom: null as string | null });
      }),
    ).subscribe(({ pledge, devis, hypotheses, enfantsNoms, conjointNom }) => {
      if (!devis) return;
      if (productType === ProductsTypeEnum.CONTINGENCY) {
        this.mergeDevisDataIntoPledge(pledge, devis, hypotheses);
      } else if (productType === ProductsTypeEnum.INSURANCE) {
        this.mergeMutuelleDevisDataIntoPledge(pledge, devis, hypotheses, enfantsNoms, conjointNom);
      }
    });
  }

  private prefillWithoutDevis(productType: ProductsTypeEnum): void {
    this.getActivePledge$().pipe(
      switchMap((pledge: PledgeRead) => {
        const accountId = Number(pledge.accountId);
        const pledgeOwnerId = pledge.owners?.[0]?.ownerId || pledge.owners?.[0]?.id;
        const productId = pledge.products?.[0]?.productId;

        // Charger hypothèses + produit en parallèle
        const hypotheses$ = pledgeOwnerId
          ? (productType === ProductsTypeEnum.INSURANCE
              ? this.productDao.getMutuelleHypotheses(accountId, Number(pledgeOwnerId))
              : this.productDao.getPrevoyanceHypotheses(accountId, Number(pledgeOwnerId))
            ).pipe(catchError(() => of(null)))
          : of(null);

        const product$ = productId
          ? this.productDao.getProductById(productId).pipe(catchError(() => of(null)))
          : of(null);

        return forkJoin({ hypotheses: hypotheses$, product: product$ }).pipe(
          map(({ hypotheses, product }) => ({ pledge, hypotheses, product })),
        );
      }),
      take(1),
      switchMap(({ pledge, hypotheses, product }) => {
        if (productType === ProductsTypeEnum.INSURANCE && hypotheses) {
          return this.resolveInsuranceNameData$(hypotheses.enfantsRattachesIds).pipe(
            map(({ enfantsNoms, conjointNom }) => ({ pledge, hypotheses, product, enfantsNoms, conjointNom })),
          );
        }
        return of({ pledge, hypotheses, product, enfantsNoms: [] as string[], conjointNom: null as string | null });
      }),
    ).subscribe(({ pledge, hypotheses, product, enfantsNoms, conjointNom }) => {
      let mergedParams: Record<string, any> = {};

      // 1. Hypothèses
      if (hypotheses) {
        mergedParams = productType === ProductsTypeEnum.INSURANCE
          ? this.buildMutuelleHypothesesParams(hypotheses, enfantsNoms, conjointNom)
          : this.buildPrevoyanceHypothesesParams(hypotheses);
      }

      // 2. Defaults produit (ne pas écraser si garanties déjà remplies)
      if (product) {
        const p = pledge.products?.[0]?.parameters as Record<string, any>;
        const alreadyFilled = productType === ProductsTypeEnum.INSURANCE
          ? (p?.hospitalisationHonorairesOptam || p?.optiqueLunettes || p?.dentaireProthese)
          : (p?.ittMontantMensuel || p?.invaliditeMontantMensuel || p?.decesPtiaMontant);

        if (!alreadyFilled) {
          const defaults = productType === ProductsTypeEnum.INSURANCE
            ? this.buildMutuelleProductDefaults(product)
            : this.buildPrevoyanceProductDefaults(product);
          const filteredDefaults = Object.fromEntries(
            Object.entries(defaults).filter(([, v]) => v != null),
          );
          mergedParams = { ...mergedParams, ...filteredDefaults };
        }
      }

      if (Object.keys(mergedParams).length === 0) return;

      const updatedPledge: PledgeRead = {
        ...pledge,
        products: pledge.products.map((p, i) =>
          i === 0 ? { ...p, parameters: { ...p.parameters, ...mergedParams } } : p,
        ),
      };
      this.subscriptionFacade.putPledge(updatedPledge).pipe(take(1)).subscribe();
    });
  }

  private buildMutuelleProductDefaults(product: any): Record<string, any> {
    return {
      hospitalisationHonorairesOptam: product.mutuelleHospitalisationHonorairesOptam != null
        ? String(product.mutuelleHospitalisationHonorairesOptam) : undefined,
      hospitalisationHonorairesNonOptam: product.mutuelleHospitalisationHonorairesNonOptam != null
        ? String(product.mutuelleHospitalisationHonorairesNonOptam) : undefined,
      hospitalisationChambreParticuliere: product.mutuelleHospitalisationChambreParticuliere != null
        ? String(product.mutuelleHospitalisationChambreParticuliere) : undefined,
      hospitalisationPrimeMaternite: product.mutuelleHospitalisationPrimeMaternite ?? undefined,
      optiqueLunettes: product.mutuelleOptiqueLunettes != null
        ? String(product.mutuelleOptiqueLunettes) : undefined,
      optiqueLentilles: product.mutuelleOptiqueLentilles != null
        ? String(product.mutuelleOptiqueLentilles) : undefined,
      optiquePlafond: product.mutuelleOptiquePlafond != null
        ? String(product.mutuelleOptiquePlafond) : undefined,
      optiqueChirurgieLaser: product.mutuelleOptiqueChirurgieLaser != null
        ? String(product.mutuelleOptiqueChirurgieLaser) : undefined,
      dentaireProthese: product.mutuelleDentaireProthese != null
        ? String(product.mutuelleDentaireProthese) : undefined,
      dentaireOrthodontieAdulte: product.mutuelleDentaireOrthodontieAdulte ?? undefined,
      dentaireOrthodontieEnfant: product.mutuelleDentaireOrthodontieEnfant ?? undefined,
      pharmaciePriseEnCharge: product.mutuellePharmaciePriseEnCharge != null
        ? String(product.mutuellePharmaciePriseEnCharge) : undefined,
      medecinesDouces: product.mutuelleMedecinesDouces ?? undefined,
      consultationsOptamCas: product.mutuelleConsultationsOptamCas != null
        ? String(product.mutuelleConsultationsOptamCas) : undefined,
      consultationsNonOptamNonCas: product.mutuelleConsultationsNonOptamNonCas != null
        ? String(product.mutuelleConsultationsNonOptamNonCas) : undefined,
      cotisationsMontant: product.mutuelleCotisationsMontant != null
        ? String(product.mutuelleCotisationsMontant) : undefined,
    };
  }

  private buildPrevoyanceProductDefaults(product: any): Record<string, any> {
    return {
      ittRenfortDos: product.prevoyanceRenfortDos != null
        ? String(product.prevoyanceRenfortDos) : undefined,
      ittAffectionPsy: product.prevoyanceAffectionPsy != null
        ? String(product.prevoyanceAffectionPsy) : undefined,
      ittMiTempsTherapeutique: product.prevoyanceMiTempsTherapeutique != null
        ? String(product.prevoyanceMiTempsTherapeutique) : undefined,
      ittFranchises: product.prevoyanceFranchiseDelai
        ? (FRANCHISE_DELAI_LABELS[product.prevoyanceFranchiseDelai] ?? product.prevoyanceFranchiseDelai) : undefined,
      ittDelaisAttente: product.prevoyanceDelaiAttente
        ? (DELAI_ATTENTE_LABELS[product.prevoyanceDelaiAttente] ?? product.prevoyanceDelaiAttente) : undefined,
      invaliditeSeuilDeclenchement: product.prevoyanceSeuilDeclenchement
        ? (SEUIL_DECLENCHEMENT_LABELS[product.prevoyanceSeuilDeclenchement] ?? '') : undefined,
      invaliditeBareme: product.prevoyanceBaremeInvalidite ?? undefined,
    };
  }


  private getDevisAndHypothesesObservables(
    productType: ProductsTypeEnum,
    accountId: number,
    devisId: number,
    ownerId: number | null,
  ): { devis$: Observable<any>; hypotheses$: Observable<any> } {
    if (productType === ProductsTypeEnum.INSURANCE) {
      return {
        devis$: this.productDao
          .getMutuelleDevisById(accountId, devisId)
          .pipe(catchError(() => of(null))),
        hypotheses$: ownerId
          ? this.productDao
              .getMutuelleHypotheses(accountId, ownerId)
              .pipe(catchError(() => of(null)))
          : of(null),
      };
    }
    // Default: CONTINGENCY
    return {
      devis$: this.productDao
        .getPrevoyanceDevisById(accountId, devisId)
        .pipe(catchError(() => of(null))),
      hypotheses$: ownerId
        ? this.productDao
            .getPrevoyanceHypotheses(accountId, ownerId)
            .pipe(catchError(() => of(null)))
        : of(null),
    };
  }

  private mergeDevisDataIntoPledge(
    pledge: PledgeRead,
    devis: PrevoyanceDevisData,
    hypotheses: PrevoyanceHypothesesData | null,
  ): void {
    // Mapping hypotheses → enums de besoins (section 6.1.3 du doc)
    const protectNeeds: string[] = [];
    if (hypotheses?.renteConjoint) protectNeeds.push('SPOUSAL_ANNUITY');
    if (hypotheses?.renteEducationEnfants) protectNeeds.push('EDUCATION_ANNUITY');

    const maintainNeeds: string[] = [];
    if (hypotheses?.preservationSalaireArretTravail) maintainNeeds.push('COVER_LOSS_OF_INCOME_DUE_WORK_STOPPAGE');
    if (hypotheses?.tnsChargesProfessionnelles) maintainNeeds.push('COVER_OVERHEAD_COSTS_DUE_WORK_STOPPAGE');

    const updatedParameters = {
      ...pledge.products[0]?.parameters,
      // ID du devis recommande pour le RCDA
      devisRecommandeId: devis.id,
      // Depuis le devis (contigency-informations)
      monthlyPayment: devis.cotisation,
      cotisationPeriode: devis.cotisationPeriode,
      // Depuis le devis (contigency-garanties)
      ittMontantMensuel: devis.ittMontantMensuel,
      ittFraisGenerauxMensuels: devis.ittFraisGenerauxMensuels,
      ittFranchises: devis.ittFranchises,
      ittRenfortDos: devis.ittRenfortDos,
      ittAffectionPsy: devis.ittAffectionPsy,
      ittDelaisAttente: devis.ittDelaisAttente,
      ittMiTempsTherapeutique: devis.ittMiTempsTherapeutique,
      invaliditeMontantMensuel: devis.invaliditeMontantMensuel,
      invaliditeBareme: devis.invaliditeBareme,
      invaliditeSeuilDeclenchement: devis.invaliditeSeuilDeclenchement,
      decesPtiaMontant: devis.decesPtiaMontant,
      cotisationMensuelle: devis.cotisationMensuelle,
      cotisationMadelin: devis.cotisationMadelin,
      commentaire: devis.commentaire,
      // Depuis les hypotheses (contigency-regime-profession)
      ...(hypotheses ? this.buildPrevoyanceHypothesesParams(hypotheses) : {}),
      // Depuis devis + hypotheses (contigency-protect-loved / maintain-income)
      ...(hypotheses ? {
        protectLovedOnesInCaseOfAccidentOrDeath:
          devis.decesPtiaMontant > 0 || hypotheses.renteConjoint || hypotheses.renteEducationEnfants,
        protectLovedOnesInCaseOfAccidentOrDeathNeeds:
          protectNeeds.length ? protectNeeds : undefined,
        maintainIncomeDuringWorkStoppage:
          hypotheses.preservationSalaireArretTravail || devis.ittMontantMensuel > 0,
        maintainIncomeDuringWorkStoppageNeeds:
          maintainNeeds.length ? maintainNeeds : undefined,
      } : {}),
    };

    const updatedPledge: PledgeRead = {
      ...pledge,
      products: pledge.products.map((p, i) =>
        i === 0 ? { ...p, parameters: updatedParameters } : p,
      ),
    };

    // Persister via PUT (meme pattern que entityService.putPledge / initializationContractForm.setCommands)
    this.subscriptionFacade.putPledge(updatedPledge).pipe(take(1)).subscribe();
  }

  private mergeAeDevisDataIntoPledge(pledge: PledgeRead, devis: any): void {
    const existingParams = pledge.products[0]?.parameters || {} as any;
    const devisQuotites: any[] = devis.insuranceQuotite || [];

    // Stocker le premiumType du devis pour le patcher dans credit-guarantees
    // (l'API DI met CI par défaut, mais le consultant peut avoir choisi CRD dans le devis)
    this.subscriptionFacade.aeDevisPremiumType = devis.premiumType || null;

    // Mapper les quotités vers loan.insuranceQuotite du tunnel
    const loanInsuranceQuotite = devisQuotites.map((q: any) => ({
      ownerId: q.ownerId,
      quotite: q.quotite != null ? q.quotite / 100 : 1,
      assureLemoineDetails: q.assureLemoineDetails || null,
      repriseGroupe: q.repriseGroupe ?? false,
    }));

    const updatedParameters = {
      ...existingParams,
      loan: {
        ...existingParams?.loan,
        ...(loanInsuranceQuotite.length > 0 ? { insuranceQuotite: loanInsuranceQuotite } : {}),
      },
      monthlyPayment: devis.cotisation,
      ...(devis.premiumType ? { premiumType: devis.premiumType } : {}),
    } as Record<string, any>;

    const updatedPledge: PledgeRead = {
      ...pledge,
      products: pledge.products.map((p, i) =>
        i === 0 ? { ...p, parameters: updatedParameters as any } : p,
      ),
    };

    // putPledge pour les quotités et cotisation (le backend les persiste)
    this.subscriptionFacade.putPledge(updatedPledge).pipe(take(1)).subscribe();
  }

  private buildMutuelleHypothesesParams(
    hypotheses: MutuelleHypothesesData, enfantsNoms: string[] = [], conjointNom: string | null = null,
  ): Record<string, any> {
    return {
      nombreAdherents: hypotheses.nombreAdherents,
      optiqueActif: hypotheses.optiqueActif,
      optiqueVisions: hypotheses.optiqueVisions,
      besoinOptiqueLunettes: hypotheses.besoinOptiqueLunettes,
      besoinOptiqueLentilles: hypotheses.besoinOptiqueLentilles,
      operationMyopie: hypotheses.operationMyopie,
      budgetOperationMyopie: hypotheses.budgetOperationMyopie,
      optiqueNotes: hypotheses.optiqueNotes,
      dentaireActif: hypotheses.dentaireActif,
      dentaireSoins: hypotheses.dentaireSoins,
      besoinDentaire: hypotheses.besoinDentaire,
      orthodontieEnfants: hypotheses.orthodontieEnfants,
      dentaireNotes: hypotheses.dentaireNotes,
      besoinSoinsCourants: hypotheses.besoinSoinsCourants,
      soinsCourantsNotes: hypotheses.soinsCourantsNotes,
      besoinHospitalisation: hypotheses.besoinHospitalisation,
      projetMaternite: hypotheses.projetMaternite,
      hospitalisationNotes: hypotheses.hospitalisationNotes,
      medecinesDoucesActif: hypotheses.medecinesDoucesActif,
      besoinMedecinesDouces: hypotheses.besoinMedecinesDouces,
      medecinesDoucesNotes: hypotheses.medecinesDoucesNotes,
      enfantsRattachesIds: hypotheses.enfantsRattachesIds,
      enfantsNoms: enfantsNoms.length ? enfantsNoms : undefined,
      conjointNom: conjointNom || undefined,
      couvertureActuelle: hypotheses.couvertureActuelle,
      natureContratExistant: hypotheses.natureContratExistant,
      motifDemande: hypotheses.motifDemande,
      eligibiliteCss: hypotheses.eligibiliteCss,
      informeRisqueCumul: hypotheses.informeRisqueCumul,
      notesGenerales: hypotheses.notesGenerales,
    };
  }

  private buildPrevoyanceHypothesesParams(hypotheses: PrevoyanceHypothesesData): Record<string, any> {
    return {
      regimeObligatoire: hypotheses.regimeObligatoire,
      beneficeNetAnnuel: hypotheses.beneficeNetAnnuel,
      fumeur: hypotheses.fumeur,
      optionDosPsy: hypotheses.optionDosPsy,
      proprietaireResidencePrincipale: hypotheses.proprietaireResidencePrincipale,
      acquisitionResidencePrincipale: hypotheses.acquisitionResidencePrincipale,
      quotiteAssuranceEmprunt: hypotheses.quotiteAssuranceEmprunt,
      renteConjoint: hypotheses.renteConjoint,
      renteEducationEnfants: hypotheses.renteEducationEnfants,
      preservationSalaireArretTravail: hypotheses.preservationSalaireArretTravail,
      montantArretTravail: hypotheses.montantArretTravail,
      preservationSalaireInvalidite: hypotheses.preservationSalaireInvalidite,
      montantInvalidite: hypotheses.montantInvalidite,
      tnsChargesProfessionnelles: hypotheses.tnsChargesProfessionnelles,
      montantChargesProfessionnelles: hypotheses.montantChargesProfessionnelles,
      avocatCollaborateur: hypotheses.avocatCollaborateur,
      dossiersPersonnels: hypotheses.dossiersPersonnels,
      notes: hypotheses.notes,
      risqueDoubleIndemnisation: hypotheses.risqueDoubleIndemnisation,
      clientInformeRisqueCumul: hypotheses.clientInformeRisqueCumul,
      justificationSouscription: hypotheses.justificationSouscription,
      justificationCommentaire: hypotheses.justificationCommentaire,
    };
  }

  private resolveEnfantsNoms(enfantsIds: number[], children: any[]): string[] {
    if (!enfantsIds?.length || !children?.length) return [];
    const idsSet = new Set(enfantsIds.map(String));
    return children
      .filter(c => idsSet.has(String(c?.relationUser?.id)))
      .map(c => `${c?.relationUser?.firstName || ''} ${c?.relationUser?.lastName || ''}`.trim())
      .filter(n => !!n);
  }

  private resolveConjointNom(couple: any[]): string | null {
    if (!couple?.length || couple.length < 2) return null;
    const spouse = couple[1];
    return `${spouse?.firstName || ''} ${spouse?.lastName || ''}`.trim() || null;
  }

  private mergeMutuelleDevisDataIntoPledge(
    pledge: PledgeRead,
    devis: MutuelleDevisData,
    hypotheses: MutuelleHypothesesData | null,
    enfantsNoms: string[] = [],
    conjointNom: string | null = null,
  ): void {
    const updatedParameters = {
      ...pledge.products[0]?.parameters,
      // ID du devis recommande pour le RCDA
      devisRecommandeId: devis.id,
      // Depuis le devis (insurance-informations)
      monthlyPayment: devis.cotisation,
      cotisationPeriode: devis.cotisationPeriode,
      // Depuis le devis (insurance-garanties)
      hospitalisationHonorairesOptam: devis.hospitalisationHonorairesOptam,
      hospitalisationHonorairesNonOptam: devis.hospitalisationHonorairesNonOptam,
      hospitalisationChambreParticuliere: devis.hospitalisationChambreParticuliere,
      hospitalisationPrimeMaternite: devis.hospitalisationPrimeMaternite,
      optiqueLunettes: devis.optiqueLunettes,
      optiqueLentilles: devis.optiqueLentilles,
      optiquePlafond: devis.optiquePlafond,
      optiqueChirurgieLaser: devis.optiqueChirurgieLaser,
      dentaireProthese: devis.dentaireProthese,
      dentaireOrthodontieAdulte: devis.dentaireOrthodontieAdulte,
      dentaireOrthodontieEnfant: devis.dentaireOrthodontieEnfant,
      pharmaciePriseEnCharge: devis.pharmaciePriseEnCharge,
      medecinesDouces: devis.medecinesDouces,
      consultationsOptamCas: devis.consultationsOptamCas,
      consultationsNonOptamNonCas: devis.consultationsNonOptamNonCas,
      cotisationsMontant: devis.cotisationsMontant,
      commentaire: devis.commentaire,
      // Depuis les hypotheses (insurance-hypotheses)
      ...(hypotheses ? this.buildMutuelleHypothesesParams(hypotheses, enfantsNoms, conjointNom) : {}),
    };

    const updatedPledge: PledgeRead = {
      ...pledge,
      products: pledge.products.map((p, i) =>
        i === 0 ? { ...p, parameters: updatedParameters } : p,
      ),
    };

    this.subscriptionFacade.putPledge(updatedPledge).pipe(take(1)).subscribe();
  }
}
